import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const ImageGallery = ({ images = [] }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    // Auto-play functionality
    useEffect(() => {
        if (!isPaused && images.length > 1) {
            const interval = setInterval(() => {
                setDirection(1);
                setCurrentIndex((prev) => (prev + 1) % images.length);
            }, 5000); // 5 seconds for better viewing experience

            return () => clearInterval(interval);
        }
    }, [isPaused, images.length, currentIndex]);

    const goToPrevious = () => {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToNext = () => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const goToSlide = (index) => {
        setDirection(index > currentIndex ? 1 : -1);
        setCurrentIndex(index);
    };

    if (!images || images.length === 0) {
        return null;
    }

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
        })
    };

    // Swipe confidence threshold
    const swipeConfidenceThreshold = 10000;
    const swipePower = (offset, velocity) => {
        return Math.abs(offset) * velocity;
    };

    return (
        <div
            className="w-full relative group select-none flex flex-col items-center gap-4"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Gallery Container - Fixed Height for Carousel Effect */}
            <div className="relative w-full h-[300px] md:h-[600px] bg-gray-100 rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <div className="absolute inset-0 flex items-center justify-center p-2 md:p-8">
                    <AnimatePresence initial={false} custom={direction}>
                        <motion.img
                            key={currentIndex}
                            src={images[currentIndex]}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{
                                x: { type: "spring", stiffness: 300, damping: 30 },
                                opacity: { duration: 0.2 }
                            }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={1}
                            onDragEnd={(e, { offset, velocity }) => {
                                const swipe = swipePower(offset.x, velocity.x);

                                if (swipe < -swipeConfidenceThreshold) {
                                    goToNext();
                                } else if (swipe > swipeConfidenceThreshold) {
                                    goToPrevious();
                                }
                            }}
                            className="absolute w-full h-full object-contain pointer-events-auto"
                            alt={`Gallery image ${currentIndex + 1}`}
                        />
                    </AnimatePresence>
                </div>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <button
                            onClick={goToPrevious}
                            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 md:bg-white/90 backdrop-blur-sm border border-gray-200 flex items-center justify-center text-gray-800 hover:bg-white hover:scale-105 shadow-md transition-all z-20"
                            aria-label="Previous image"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>

                        <button
                            onClick={goToNext}
                            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 md:bg-white/90 backdrop-blur-sm border border-gray-200 flex items-center justify-center text-gray-800 hover:bg-white hover:scale-105 shadow-md transition-all z-20"
                            aria-label="Next image"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>

                        {/* Counter */}
                        <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-md text-white text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-white/20 z-20">
                            {currentIndex + 1} / {images.length}
                        </div>
                    </>
                )}
            </div>

            {/* Thumbnails / Dots */}
            {images.length > 1 && (
                <div className="flex justify-center items-center gap-2 flex-wrap px-4">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-300 rounded-full ${index === currentIndex
                                ? 'bg-cyan-600 w-8 h-2'
                                : 'bg-gray-300 hover:bg-gray-400 w-2 h-2'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ImageGallery;
